import React from "react";

import { css } from "@emotion/css";
import Drawer from "@mui/material/Drawer";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { usePatientFromURL } from "@/hooks/usePatientIdFromURL.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { IdentityValidation } from "@/models/identity/IdentityValidationModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { toDateTime } from "@/utils/dateUtils.ts";

export const ValidateIdentityDrawer: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const patient = usePatientFromURL();
  const me = Queries.user.usePractitionerMe({});

  const validateOrRejectQuery = Queries.practitioner.useValidateOrRejectIdentity();

  if (me.qualification !== "doctor" || !patient) {
    return null;
  }

  if (patient.identityLastname === undefined) {
    // This excludes the patients created when the notEntered status was not implemented
    return null;
  }

  const identity = patient as IdentityValidation;
  const submitting = validateOrRejectQuery.isPending;
  const birthLocation = identity.birthLocation
    ? `${identity.birthLocation?.town} (${identity.birthLocation?.postCode.slice(0, 2)})`
    : identity.birthCountry;
  const validateOrReject = (action: "validate" | "reject") =>
    validateOrRejectQuery.mutate({ patientId: patient.id, action });

  return (
    <Drawer id="validate-identity-drawer" open={patient.identityStatus === "notValidated"} anchor="right">
      <CardHeader variant="contained" title={t("identityValidation.title")} />
      <CardContent className={styles.content}>
        <Typography>{t("identityValidation.subtitle")}</Typography>
        <table>
          <tbody>
            <PatientLine
              labelClassname={styles.lineLabel}
              label={t("profile.identityFirstnames")}
              value={identity.identityFirstnames.join(", ")}
            />
            <PatientLine
              labelClassname={styles.lineLabel}
              label={t("profile.identityLastname")}
              value={identity.identityLastname}
            />
            <PatientLine
              labelClassname={styles.lineLabel}
              label={t("profile.gender")}
              value={identity.gender === "male" ? t("profile.male") : t("profile.female")}
            />
            <PatientLine
              labelClassname={styles.lineLabel}
              label={t("profile.birthDate")}
              value={toDateTime(identity.birthDate).toLocaleString(DateTime.DATE_SHORT)}
            />
            {birthLocation ? (
              <PatientLine labelClassname={styles.lineLabel} label={t("profile.birthPlace")} value={birthLocation} />
            ) : null}
          </tbody>
        </table>
      </CardContent>
      <CardFooter className={styles.footer}>
        <Button
          variant="outlined"
          id={`identity-reject-${identity.id}`}
          data-action="inclusion.identity.reject"
          pending={submitting}
          onClick={() => validateOrReject("reject")}
        >
          {t("button.reject")}
        </Button>
        <Button
          variant="contained"
          id={`identity-validate-${identity.id}`}
          data-action="inclusion.identity.validate"
          pending={submitting}
          onClick={() => validateOrReject("validate")}
        >
          {t("button.accept")}
        </Button>
      </CardFooter>
    </Drawer>
  );
};

type PatientLineProps = {
  label: string;
  value: string;
  labelClassname?: string;
};

const PatientLine: React.FC<PatientLineProps> = ({ label, value, labelClassname }) => {
  return (
    <tr>
      <td className={labelClassname}>
        <Typography variant="body">{label}</Typography>
      </td>
      <td>
        <Typography variant="body">{value}</Typography>
      </td>
    </tr>
  );
};

const makeStyles = (theme: Theme) => ({
  content: css({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(8),
  }),
  footer: css({
    paddingBottom: theme.spacing(8),
  }),
  item: css({
    padding: theme.spacing(12),
    marginBottom: theme.spacing(8),
  }),
  itemTitle: css({
    paddingBottom: theme.spacing(8),
  }),
  lineLabel: css({
    paddingRight: theme.spacing(12),
  }),
  list: css({
    padding: theme.spacing(8, 2),
    gap: theme.spacing(8),
  }),
});
